export type NodeType
  = 'site'
  | 'drive'
  | 'folder'
  | 'file'
  | 'placeholder'

export type ParentIds = {
  siteId?: string
  driveId?: string
  itemId?: string
}

type TreeNodeProps = {
  id: string
  label: string
  type: NodeType
  webUrl: string
  hasChildren: boolean
  parentIds: ParentIds
}

type TreeNodeBase<R extends {} = TreeNodeProps> = R & {
  children?: TreeNodeBase<R>[]
}

export type TreeNode = TreeNodeBase<TreeNodeProps>

export const isPlaceholderNode = (node: TreeNode | null): boolean => !!(
  node && node.type === 'placeholder'
)

export const isNodeTypeFolderLike = (node: TreeNode): boolean => [
  'site', 'drive', 'folder'
].includes(node.type)

export const isChildrenLoaded = (node: TreeNode): boolean => !!(
  !node?.hasChildren
  || node?.children?.length === 0
  || node?.children?.length && !isPlaceholderNode(node.children[0])
)

const SHAREABLE_LOCATION_TYPES = ['drive', 'folder']

export const isShareableLocation = (node: TreeNode): boolean => (
  SHAREABLE_LOCATION_TYPES.includes(node.type)
)

let placeholderIdCounter = 0

const newPlaceholderNodeId = (): string => {
  return `placeholder-${placeholderIdCounter++}`
}

export const resetPlaceholderNodeIdCounter = (): void => {
  placeholderIdCounter = 0
}

export const newPlaceholderNode = (label: string = "Loading..."): TreeNode => ({
  id: newPlaceholderNodeId(),
  label,
  type: 'placeholder' as NodeType,
  webUrl: '',
  hasChildren: false,
  parentIds: {},
} as TreeNode)

export const getParentNodeId = (node: TreeNode): string | undefined => (
  node.parentIds.itemId || node.parentIds.driveId || node.parentIds.siteId
)

export const getParentNode = (
  treeData: TreeNode[],
  node: TreeNode
): TreeNode | null => {
  const parentNodeId = getParentNodeId(node)
  if (!parentNodeId) return null
  return findNodeById(treeData, parentNodeId)
}

export const findNodeById = (nodes: TreeNode[], id: string): TreeNode | null => {
  for (const node of nodes) {
    if (!node) continue
    if (node.id === id) return node
    if (node.children) {
      const found = findNodeById(node.children, id)
      if (found) return found
    }
  }
  return null
}
